import {
  createContext,
  FC,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type {
  RedirectModalStateContextData,
  RedirectModalStateProviderProps,
} from './types';
import { ServiceRedirectModal } from '../../components';

const RedirectModalStateContext = createContext<RedirectModalStateContextData>(
  {} as RedirectModalStateContextData,
);

const RedirectModalStateProvider: FC = ({ children }) => {
  const [openRedirectModal, setOpenRedirectModal] = useState(false);

  const toggleRedirectModal = () => {
    setOpenRedirectModal(prev => !prev);
  };

  const [redirectModalStateData, setRedirectModalStateData] =
    useState<RedirectModalStateProviderProps>({
      redirectEnum: undefined,
      title: undefined,
    });

  const changeRedirectModalStateData = useCallback(
    ({ redirectEnum, title }: RedirectModalStateProviderProps) => {
      setRedirectModalStateData({
        redirectEnum,
        title,
      });
    },
    [setRedirectModalStateData],
  );

  const valueContextProvider = useMemo(
    () => ({
      ...redirectModalStateData,
      changeRedirectModalStateData,
      toggleRedirectModal,
    }),
    [redirectModalStateData, changeRedirectModalStateData, toggleRedirectModal],
  );

  return (
    <RedirectModalStateContext.Provider value={valueContextProvider}>
      {children}
      <ServiceRedirectModal
        openModal={openRedirectModal}
        toggleModal={toggleRedirectModal}
      />
    </RedirectModalStateContext.Provider>
  );
};

function useRedirectModalState(): RedirectModalStateContextData {
  const context = useContext(RedirectModalStateContext);

  if (!context) {
    throw new Error(
      'useRedirectModalState must be used within RedirectModalStateProvider',
    );
  }

  return context;
}

export { useRedirectModalState, RedirectModalStateProvider };
